import { ChevronLeft, ChevronRight } from 'components/icons';
import { Tabs, TabsList, TabsTrigger } from 'components/ui/tabs';
import { ComponentProps, ReactNode, useEffect, useRef, useState } from 'react';
import { cn } from 'utils/cn';

/**
 * The underlined tab strip: the case-page tabs, the timecard MAIN/DRAFT sub-tabs and the global
 * timecard list. A thin layer over the kit tabs that owns the strip's look, so every strip shares
 * one ink, hover and indicator. `TabsGallery` adds routing and panels on top of it.
 */
export function LineTabs({ className, ...props }: ComponentProps<typeof Tabs>) {
    return <Tabs className={cn('block gap-0', className)} {...props} />;
}

// The height must carry the kit's variant prefix to replace its h-9: a 36px list under the 46px tabs
// overflows, and the list clips that overflow. pb-0.5 is the band under the tabs the underline fills,
// so it rests on the border line; the trigger's bottom-[-3px] clears its 1px border, which `bottom` is
// measured inside. At some browser zoom factors the underline still ends a sub-pixel past the band,
// which overflow-x-auto alone would turn into a vertical scrollbar: overflow-y-hidden drops it.
const LIST_CLASS =
    'w-full justify-start gap-0.5 overflow-x-auto overflow-y-hidden p-0 pb-0.5 group-data-horizontal/tabs:h-auto new:gap-1';

type LineTabsListProps = Omit<ComponentProps<typeof TabsList>, 'variant'> & {
    /**
     * A strip that scrolls behind chevrons instead of a scrollbar: the bar would eat half of a compact
     * strip's height. Each chevron overlays its edge of the strip and shows only while there are tabs
     * past that edge; `className` lands on the wrapper, which carries the border in this mode. The
     * labels name the chevrons for assistive tech; they are passed in so this component stays clear of
     * the intl runtime, which tests of the strips' pages stub out.
     */
    arrows?: { back: string; next: string };
};

export function LineTabsList({ className, arrows, ...props }: LineTabsListProps) {
    if (arrows) {
        return <ArrowScrolledTabsList labels={arrows} className={className} {...props} />;
    }
    return <TabsList variant="line" className={cn(LIST_CLASS, 'border-divider border-b', className)} {...props} />;
}

// Classic shows the active tab with `scrollIntoView`. The new design moves the strip alone, and only sideways:
// `scrollIntoView` scrolls every scroller above the strip too, and its record page, which scrolls as one below
// `sm`, would open part-way down its band; and the browser takes its target for where the next Tab key starts
// from, so the first Tab on a freshly opened page would land after the strip, past the skip control and
// everything else before it.
const revealActiveTab = (list: HTMLElement) => {
    const tab = list.querySelector('[data-active]');
    if (!tab) return;
    if (document.documentElement.dataset.design !== 'new') {
        tab.scrollIntoView({ inline: 'nearest', block: 'nearest' });
        return;
    }
    const strip = list.getBoundingClientRect();
    const { left, right } = tab.getBoundingClientRect();
    // `scrollLeft` grows to the right whichever way the strip reads.
    if (left < strip.left) list.scrollLeft += left - strip.left;
    else if (right > strip.right) list.scrollLeft += right - strip.right;
};

function ArrowScrolledTabsList({
    labels,
    className,
    ...props
}: Omit<LineTabsListProps, 'arrows'> & { labels: { back: string; next: string } }) {
    const listRef = useRef<HTMLDivElement>(null);
    const [reach, setReach] = useState({ start: false, end: false });

    useEffect(() => {
        const list = listRef.current;
        if (!list) return;
        const update = () => {
            // Negative in a right-to-left strip, which scrolls from its right edge. Left to right it is read as
            // it is, so an overscroll that bounces below zero shows no arrow.
            const offset = getComputedStyle(list).direction === 'rtl' ? -list.scrollLeft : list.scrollLeft;
            const start = offset > 0;
            const end = offset + list.clientWidth < list.scrollWidth - 1;
            setReach((current) => (current.start === start && current.end === end ? current : { start, end }));
        };
        update();
        // The strip may open with its active tab past the edge; the observer below only sees later changes.
        revealActiveTab(list);
        list.addEventListener('scroll', update, { passive: true });
        // Tabs come and go and the strip resizes with the modal, neither of which fires a scroll event.
        const resized = new ResizeObserver(update);
        resized.observe(list);
        const changed = new MutationObserver((mutations) => {
            update();
            // A tab that just became active (one the user added, say) may sit past the edge.
            if (mutations.some((mutation) => mutation.attributeName === 'data-active')) {
                revealActiveTab(list);
            }
        });
        // characterData: a renamed tab widens by a text-node edit, which resizes the tab but not the list.
        changed.observe(list, {
            childList: true,
            characterData: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['data-active'],
        });
        return () => {
            list.removeEventListener('scroll', update);
            resized.disconnect();
            changed.disconnect();
        };
    }, []);

    // Reduced motion jumps instead of gliding.
    const scrollBy = (direction: -1 | 1) => {
        const list = listRef.current;
        if (list) {
            // `left` is physical: the way to the strip's end is negative right to left.
            const towardsEnd = getComputedStyle(list).direction === 'rtl' ? -1 : 1;
            const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            list.scrollBy({
                left: direction * towardsEnd * list.clientWidth * 0.6,
                behavior: reduce ? 'auto' : 'smooth',
            });
        }
    };

    return (
        <div className={cn('border-divider relative w-full min-w-0 border-b', className)}>
            <TabsList
                ref={listRef}
                variant="line"
                className={cn(LIST_CLASS, '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden')}
                {...props}
            />
            {reach.start ? (
                <ScrollArrow side="start" label={labels.back} onClick={() => scrollBy(-1)}>
                    <ChevronLeft className="rtl:-scale-x-100" />
                </ScrollArrow>
            ) : null}
            {reach.end ? (
                <ScrollArrow side="end" label={labels.next} onClick={() => scrollBy(1)}>
                    <ChevronRight className="rtl:-scale-x-100" />
                </ScrollArrow>
            ) : null}
        </div>
    );
}

// Overlays the strip's edge: the button itself is opaque, and the tabs beside it fade out under a short
// gradient, which is what says there is more to scroll.
function ScrollArrow({
    side,
    label,
    onClick,
    children,
}: Pick<ComponentProps<'button'>, 'onClick' | 'children'> & { side: 'start' | 'end'; label: string }) {
    const start = side === 'start';
    return (
        <div
            className={cn('pointer-events-none absolute inset-y-0 flex', start ? 'start-0' : 'end-0 flex-row-reverse')}
        >
            <button
                type="button"
                aria-label={label}
                onClick={onClick}
                className="bg-paper text-grey-500 hover:text-fg pointer-events-auto flex cursor-pointer items-center px-0.5 text-[1.2rem]"
            >
                {children}
            </button>
            <span
                aria-hidden
                className={cn(
                    'from-paper w-6 to-transparent',
                    start ? 'bg-linear-to-r rtl:bg-linear-to-l' : 'bg-linear-to-l rtl:bg-linear-to-r'
                )}
            />
        </div>
    );
}

// Inactive tabs use solid grey-600 ink, not a lowered opacity: opacity 0.5 lands on the same grey the
// kit uses for disabled controls, so "not selected" and "disabled" would look alike. The hover wash
// carries the list-variant prefix because the kit pins a line-variant trigger to bg-transparent with
// a more specific selector than a plain hover: class; the active tab needs its own, again more
// specific, hover rule because the kit pins the active line-variant trigger to bg-transparent too.
// The label is stacked over an invisible semibold twin, so the tab already has its selected width
// at rest: the 500-to-600 weight change would otherwise widen the tab and shift its siblings. The new
// design's default strip is 40px with the selected tab in the deep green.
type LineTabProps = ComponentProps<typeof TabsTrigger> & {
    /** Rendered after the label, outside the width reservation. */
    icon?: ReactNode;
    /** Off for an interactive label (the BD dropdown menu), whose hidden twin would not be harmless. */
    reserveSelectedWidth?: boolean;
    /** `compact` is the 36px strip inside popovers and modals; the ink, hover and underline stay the same. */
    size?: 'default' | 'compact';
};

export function LineTab({
    className,
    children,
    icon,
    reserveSelectedWidth = true,
    size = 'default',
    ...props
}: LineTabProps) {
    const label = reserveSelectedWidth ? (
        <span className="grid">
            <span className="col-start-1 row-start-1">{children}</span>
            <span aria-hidden className="invisible col-start-1 row-start-1 font-semibold">
                {children}
            </span>
        </span>
    ) : (
        children
    );
    return (
        <TabsTrigger
            className={cn(
                // `new:` outranks `hover:`, so the new design's colour at rest restates the hover one after it.
                'text-grey-600 new:text-foreground-tertiary hover:text-fg new:hover:text-fg data-active:text-fg new:data-active:text-primary-strong min-h-[46px] flex-none cursor-pointer rounded-t-sm rounded-b-none px-3.5 py-3 font-medium data-active:font-semibold',
                'group-data-[variant=line]/tabs-list:hover:bg-grey-100 group-data-[variant=line]/tabs-list:data-active:hover:bg-grey-100',
                'after:bg-primary group-data-horizontal/tabs:after:bottom-[-3px]',
                size === 'compact' ? 'min-h-9 px-2 py-1 text-[0.8rem]' : 'new:min-h-10 new:px-3 new:py-0',
                className
            )}
            {...props}
        >
            {label}
            {icon}
        </TabsTrigger>
    );
}
